'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'motion/react';
import React, { startTransition, useDeferredValue, useEffect, useRef, useState } from 'react';
const CONSTANTS = {
  BAR_HEIGHT: 36,
  HEADER_HEIGHT: 36,
  MOBILE_BREAKPOINT: 768,
  DEFAULT_WIDTH: 480,
  MIN_WIDTH: 320,
  ASPECT_RATIO: 16 / 9,
  CORNER_PADDING: 20,
  ANIMATION_DURATION: 0.3
};
const containerVariants = {
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'tween',
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      type: 'tween',
      duration: 0.25,
      ease: 'easeIn'
    }
  }
};
const MediaFrame = t0 => {
  const $ = _c(46);
  const {
    isVisible,
    title,
    initialCornerPosition: t1,
    children,
    onClose
  } = t0;
  const initialCornerPosition = t1 === undefined ? "bottom-right" : t1;
  const [isHidden, setIsHidden] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useState(false);
  const [hasErrorOccurred] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  const deferredWindowWidth = useDeferredValue(windowWidth);
  const [cornerPosition, setCornerPosition] = useState(initialCornerPosition);
  const containerRef = useRef(null);
  const animationTimerRef = useRef(null);
  let t2;
  if ($[0] !== deferredWindowWidth || $[1] !== isMinimized || $[2] !== isMobile) {
    t2 = () => {
      let width = isMinimized ? CONSTANTS.MIN_WIDTH : Math.min(deferredWindowWidth - CONSTANTS.CORNER_PADDING * 2, CONSTANTS.DEFAULT_WIDTH);
      if (isMobile) {
        width = isMinimized ? CONSTANTS.MIN_WIDTH : Math.min(deferredWindowWidth - CONSTANTS.CORNER_PADDING * 2, CONSTANTS.DEFAULT_WIDTH);
      }
      const height = width / CONSTANTS.ASPECT_RATIO;
      return {
        width,
        height
      };
    };
    $[0] = deferredWindowWidth;
    $[1] = isMinimized;
    $[2] = isMobile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getDimensions = t2;
  let t3;
  if ($[4] !== getDimensions) {
    t3 = getDimensions();
    $[4] = getDimensions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    width: playerWidth,
    height: playerHeight
  } = t3;
  const getContainerHeight = () => {
    if (isMobile && isHidden) {
      return CONSTANTS.BAR_HEIGHT;
    }
    return playerHeight + CONSTANTS.HEADER_HEIGHT;
  };
  let t4;
  if ($[6] !== cornerPosition || $[7] !== isHidden || $[8] !== isMobile || $[9] !== playerWidth) {
    t4 = () => {
      if (isMobile && isHidden) {
        return {
          top: "auto",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%"
        };
      }
      if (isMobile) {
        return {
          top: "auto",
          bottom: `${CONSTANTS.CORNER_PADDING / 2}px`,
          left: 0,
          right: 0,
          width: "100%",
          margin: "0 auto",
          maxWidth: `${playerWidth}px`
        };
      }
      if (cornerPosition === "bottom-right") {
        return {
          top: "auto",
          bottom: `${CONSTANTS.CORNER_PADDING}px`,
          left: "auto",
          right: `${CONSTANTS.CORNER_PADDING}px`,
          width: `${playerWidth}px`
        };
      }
      return {
        top: "auto",
        bottom: `${CONSTANTS.CORNER_PADDING}px`,
        left: `${CONSTANTS.CORNER_PADDING}px`,
        right: "auto"
      };
    };
    $[6] = cornerPosition;
    $[7] = isHidden;
    $[8] = isMobile;
    $[9] = playerWidth;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const getPositionStyle = t4;
  let t5;
  if ($[11] !== animating || $[12] !== cornerPosition) {
    t5 = () => {
      if (animating) {
        return;
      }
      startTransition(() => {
        setAnimating(true);
        setCornerPosition(cornerPosition === "bottom-right" ? "bottom-left" : "bottom-right");
        if (animationTimerRef.current) {
          clearTimeout(animationTimerRef.current);
        }
        animationTimerRef.current = setTimeout(() => {
          setAnimating(false);
          animationTimerRef.current = null;
        }, CONSTANTS.ANIMATION_DURATION * 1000);
      });
    };
    $[11] = animating;
    $[12] = cornerPosition;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const toggleCornerPosition = t5;
  let t6;
  if ($[14] !== isMinimized) {
    t6 = () => {
      startTransition(() => {
        setIsMinimized(!isMinimized);
        setIsHidden(false);
      });
    };
    $[14] = isMinimized;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const toggleMinimize = t6;
  let t7;
  if ($[16] !== isHidden) {
    t7 = () => {
      startTransition(() => {
        setIsHidden(!isHidden);
      });
    };
    $[16] = isHidden;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const toggleHiddenState = t7;
  let t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleResize = () => {
        const newWindowWidth = window.innerWidth;
        startTransition(() => {
          setWindowWidth(newWindowWidth);
          setIsMobile(newWindowWidth < CONSTANTS.MOBILE_BREAKPOINT);
        });
      };
      handleResize();
      window.addEventListener("resize", handleResize, {
        passive: true
      });
      return () => {
        window.removeEventListener("resize", handleResize);
        if (animationTimerRef.current) {
          clearTimeout(animationTimerRef.current);
        }
      };
    };
    t9 = [];
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  const getTransitionString = _temp;
  const t10 = `${playerWidth}px`;
  const t11 = `${getContainerHeight()}px`;
  const t12 = getTransitionString();
  const t13 = isMobile ? isHidden ? "0" : "6px 6px 0 0" : "6px";
  let t14;
  if ($[20] !== getPositionStyle) {
    t14 = getPositionStyle();
    $[20] = getPositionStyle;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t13 || $[25] !== t14) {
    t15 = {
      position: "fixed",
      overflow: "hidden",
      width: t10,
      height: t11,
      transition: t12,
      borderRadius: t13,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
      zIndex: 9999,
      ...t14
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const containerStyle = t15;
  let t16;
  if ($[27] !== animating || $[28] !== cornerPosition || $[29] !== hasErrorOccurred || $[30] !== isHidden || $[31] !== isMinimized || $[32] !== isMobile || $[33] !== onClose || $[34] !== title || $[35] !== toggleCornerPosition || $[36] !== toggleHiddenState || $[37] !== toggleMinimize) {
    t16 = () => {
      const headerStyle = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 12px",
        height: `${CONSTANTS.HEADER_HEIGHT}px`,
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        color: "white",
        userSelect: "none"
      };
      const titleStyle = {
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: "14px",
        fontWeight: 500,
        whiteSpace: "nowrap"
      };
      const buttonStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 4px",
        padding: "4px",
        cursor: "pointer",
        color: "white",
        border: "none",
        background: "none",
        touchAction: "manipulation",
        transition: "opacity 0.2s ease"
      };
      return <header style={headerStyle}><div style={{
          display: "flex",
          alignItems: "center",
          width: "100%"
        }}>{hasErrorOccurred && <>ERROR</>}<div style={titleStyle}><p>{title}</p></div><div style={{
            display: "flex"
          }}>{isMobile && <button style={buttonStyle} onClick={toggleHiddenState} title="Toggle visibility">{isHidden ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>}</button>}{!isMobile && <button style={{
              ...buttonStyle,
              opacity: animating ? 0.5 : 1,
              cursor: animating ? "default" : "pointer"
            }} onClick={toggleCornerPosition} disabled={animating} title="Toggle corner position">{cornerPosition === "bottom-left" ? <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 10 4 15 9 20" /><path d="M20 4v7a4 4 0 0 1-4 4H4" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 10 20 15 15 20" /><path d="M4 4v7a4 4 0 0 0 4 4h12" /></svg>}</button>}{(isMobile && !isHidden || !isMobile) && <button style={buttonStyle} onClick={toggleMinimize} title={isMinimized ? "Maximize" : "Minimize"}>{isMinimized ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14h6v6" /><path d="M20 10h-6V4" /><path d="M14 10l7-7" /><path d="M3 21l7-7" /></svg>}</button>}<button style={buttonStyle} onClick={onClose} title="Close"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button></div></div></header>;
    };
    $[27] = animating;
    $[28] = cornerPosition;
    $[29] = hasErrorOccurred;
    $[30] = isHidden;
    $[31] = isMinimized;
    $[32] = isMobile;
    $[33] = onClose;
    $[34] = title;
    $[35] = toggleCornerPosition;
    $[36] = toggleHiddenState;
    $[37] = toggleMinimize;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const headerController = t16;
  let t17;
  if ($[39] !== children || $[40] !== containerStyle || $[41] !== headerController || $[42] !== isVisible) {
    t17 = isVisible && <motion.div ref={containerRef} initial="hidden" animate="visible" exit="hidden" variants={containerVariants} style={containerStyle}><div style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%"
      }}>{headerController()}{children}</div></motion.div>;
    $[39] = children;
    $[40] = containerStyle;
    $[41] = headerController;
    $[42] = isVisible;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t17) {
    t18 = <AnimatePresence>{t17}</AnimatePresence>;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
};
export default MediaFrame;
function _temp() {
  return `all ${CONSTANTS.ANIMATION_DURATION}s cubic-bezier(0.25, 0.1, 0.25, 1.0)`;
}